/**
 * Date/time helpers for API timestamps.
 *
 * WHAT: parsing of the backend's naive-UTC ISO strings (SQLAlchemy emits
 * `2026-09-12T17:00:00` with no zone suffix), relative "3m ago" labels,
 * zone-aware display, and the open-house wall-clock helpers that bridge
 * `<input type="datetime-local">` values and ISO instants in a zone.
 *
 * WHY: the first five helpers lived as private functions inside the admin
 * PropertiesPage. The open house feature needs the same parsing in the
 * kiosk countdown, the admin list and the public /open-house strip, so
 * they moved here with identical behaviour.
 *
 * Import from '@/utils/dateTime' directly. `@/utils` already exports a
 * different `formatDateTime` (formatters.ts, "Jan 15, 10:30 AM"); this
 * module is deliberately not re-exported from the utils index so the two
 * names never collide.
 */

/** Wall-clock zone every open house is entered in (labelled "ET" in UI). */
export const ET_TZ = 'America/New_York';

const MS_PER_MINUTE = 60_000;
const MINUTES_PER_HOUR = 60;
const HOURS_PER_DAY = 24;
const DAYS_BEFORE_ABSOLUTE_DATE = 30;
// ICU 72+ renders "1:00 PM" with a narrow no-break space before the
// meridiem; normalise so slicing on " PM" is stable across engines.
const NARROW_SPACES = /[\u202f\u00a0]/g;
const MERIDIEM_LENGTH = 'PM'.length;
const MERIDIEM_WITH_SPACE_LENGTH = MERIDIEM_LENGTH + 1;

/**
 * Append "Z" to a naive ISO string so `new Date()` reads it as UTC
 * instead of the browser's local zone. Strings that already carry an
 * offset or "Z" are returned untouched.
 */
export function normalizeApiIso(iso: string): string {
    const trimmed = iso.trim();
    if (/[zZ]$|[+-]\d{2}:?\d{2}$/.test(trimmed)) return trimmed;
    return `${trimmed}Z`;
}

export function parseApiDate(iso: string): Date {
    return new Date(normalizeApiIso(iso));
}

const DATE_ONLY_PATTERN = /^(\d{4})-(\d{2})-(\d{2})$/;

/**
 * A date-only API value ("2026-10-28") as a local calendar day, e.g.
 * "Tue, Oct 28". `new Date("2026-10-28")` would read it as UTC midnight
 * and show the previous day west of Greenwich.
 */
export function formatDateOnly(isoDate: string | null | undefined): string {
    const match = DATE_ONLY_PATTERN.exec(isoDate || '');
    if (!match) return isoDate || '—';
    const day = new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
    return day.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
}

/** Today as the "YYYY-MM-DD" value an `<input type="date">` expects, in local time. */
export function todayDateInputValue(): string {
    const now = new Date();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    return `${now.getFullYear()}-${month}-${day}`;
}

/** Browser-local `toLocaleString()` of an API timestamp, or an em dash. */
export function formatDateTime(iso: string | null): string {
    if (!iso) return '—';
    return parseApiDate(iso).toLocaleString();
}

/** "just now" / "12m ago" / "in 3h" / absolute date beyond ~30 days. */
export function relativeTime(iso: string | null): string {
    if (!iso) return 'never';
    const then = parseApiDate(iso).getTime();
    const diff = Date.now() - then;
    if (diff < 0) {
        const minsUntil = Math.ceil(Math.abs(diff) / MS_PER_MINUTE);
        if (minsUntil < 1) return 'now';
        if (minsUntil < MINUTES_PER_HOUR) return `in ${minsUntil}m`;
        const hoursUntil = Math.floor(minsUntil / MINUTES_PER_HOUR);
        if (hoursUntil < HOURS_PER_DAY) return `in ${hoursUntil}h`;
        return formatDateTime(iso);
    }
    const mins = Math.floor(diff / MS_PER_MINUTE);
    if (mins < 1) return 'just now';
    if (mins < MINUTES_PER_HOUR) return `${mins}m ago`;
    const hours = Math.floor(mins / MINUTES_PER_HOUR);
    if (hours < HOURS_PER_DAY) return `${hours}h ago`;
    const days = Math.floor(hours / HOURS_PER_DAY);
    if (days < DAYS_BEFORE_ABSOLUTE_DATE) return `${days}d ago`;
    return parseApiDate(iso).toLocaleDateString();
}

/** "9/12/26, 1:00 PM America/New_York" — falls back to local time on a bad zone. */
export function formatDateTimeInZone(iso: string | null | undefined, timeZone: string | null | undefined): string {
    if (!iso) return '—';
    const date = parseApiDate(iso);
    if (Number.isNaN(date.getTime())) return '—';
    if (!timeZone) return date.toLocaleString();

    try {
        const formatted = new Intl.DateTimeFormat('en-US', {
            dateStyle: 'short',
            timeStyle: 'short',
            timeZone,
        }).format(date);
        return `${formatted} ${timeZone}`;
    } catch {
        return date.toLocaleString();
    }
}

/** "1:00 PM" in the given zone (no leading zero). */
function formatClock(date: Date, timeZone: string): string {
    try {
        return new Intl.DateTimeFormat('en-US', {
            hour: 'numeric',
            minute: '2-digit',
            hour12: true,
            timeZone,
        })
            .format(date)
            .replace(NARROW_SPACES, ' ');
    } catch {
        return date
            .toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })
            .replace(NARROW_SPACES, ' ');
    }
}

/**
 * "1:00–4:00 PM" — the meridiem is only shown once when both ends share
 * it, matching the server-rendered `schedule_label`.
 */
export function formatTimeRange(
    startIso: string | null | undefined,
    endIso: string | null | undefined,
    timeZone: string = ET_TZ,
): string {
    if (!startIso || !endIso) return '—';
    const start = parseApiDate(startIso);
    const end = parseApiDate(endIso);
    if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return '—';

    const startClock = formatClock(start, timeZone);
    const endClock = formatClock(end, timeZone);
    const sameMeridiem = startClock.slice(-MERIDIEM_LENGTH) === endClock.slice(-MERIDIEM_LENGTH);
    const startLabel = sameMeridiem ? startClock.slice(0, -MERIDIEM_WITH_SPACE_LENGTH) : startClock;
    return `${startLabel}–${endClock}`;
}

function pad2(value: number): string {
    return String(value).padStart(2, '0');
}

function localInputValueFromLocalClock(date: Date): string {
    return `${date.getFullYear()}-${pad2(date.getMonth() + 1)}-${pad2(date.getDate())}`
        + `T${pad2(date.getHours())}:${pad2(date.getMinutes())}`;
}

/**
 * ISO instant → 'YYYY-MM-DDTHH:MM' wall-clock in `timeZone`, the exact
 * value an `<input type="datetime-local">` accepts and the backend's
 * `starts_at_local` / `ends_at_local` expect. Empty string on bad input.
 */
export function toLocalInputValue(iso: string | null | undefined, timeZone: string = ET_TZ): string {
    if (!iso) return '';
    const date = parseApiDate(iso);
    if (Number.isNaN(date.getTime())) return '';

    try {
        const parts = new Intl.DateTimeFormat('en-US', {
            timeZone,
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
            hourCycle: 'h23',
        }).formatToParts(date);
        const part = (type: Intl.DateTimeFormatPartTypes): string =>
            parts.find((entry) => entry.type === type)?.value ?? '';
        // Some engines still emit "24" at midnight despite hourCycle h23.
        const hour = part('hour') === '24' ? '00' : part('hour');
        return `${part('year')}-${part('month')}-${part('day')}T${hour}:${part('minute')}`;
    } catch {
        return localInputValueFromLocalClock(date);
    }
}

/**
 * Join separate date ('YYYY-MM-DD') and time ('HH:MM') inputs into the
 * 'YYYY-MM-DDTHH:MM' local value the create form posts. Empty string
 * when either half is missing so validation can flag it.
 */
export function combineLocalDateTime(date: string, time: string): string {
    if (!date || !time) return '';
    return `${date}T${time}`;
}
